import React, { useEffect, useState } from 'react';
// form
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { IDepartment } from 'src/@types/apiResponseTypes';
import Iconify from 'src/components/iconify';
import { useStores } from 'src/models';

interface DepartmentSelectProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value: number | undefined | null;
  onChange: (value: number | undefined | null) => void;
}

function DepartmentSelect({
  label = '부서',
  value,
  onChange,
  error,
  ...props
}: DepartmentSelectProps) {
  const { departments } = useStores().departmentStore;

  return (
    <FormControl
      fullWidth
      size="small"
      error={error}
      sx={{ minWidth: 250, maxWidth: '100%', flex: 1 }}
    >
      <TextField
        error={error}
        select
        fullWidth
        size="small"
        defaultValue={''}
        label={label}
        value={value ? `${value}` : ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        InputProps={{
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(undefined);
                }}
                sx={{
                  p: '1px',
                  marginRight: 1.5,
                  backgroundColor: '#f0f0f0',
                }}
              >
                <Iconify icon="eva:close-fill" width={12} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        SelectProps={{
          sx: { textTransform: 'capitalize' },
        }}
        {...props}
      >
        <MenuItem value={undefined} sx={{ display: 'none' }}></MenuItem>
        {departments.map((dept) => {
          const hq = dept?.pnm || dept?.deptPnm;
          const upper = hq ? String(hq).toUpperCase() : '';
          const isCEO = upper.includes('CEO');
          const hqText = isCEO ? '' : String(upper);

          return (
            <MenuItem key={`dept-select-${dept.deptSid}`} value={dept.deptSid}>
              <Box
                display="flex"
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'flex-start'}
                gap={1}
              >
                {hqText && (
                  <Typography variant="caption" color="text.secondary">
                    {`${hqText} `}
                  </Typography>
                )}
                {dept?.deptNm || ''}
              </Box>
            </MenuItem>
          );
        })}
      </TextField>
    </FormControl>
  );
}

export default DepartmentSelect;

export const DepartmentLabel = ({
  deptSid,
  orientation = 'horizontal',
}: {
  deptSid?: number;
  orientation?: 'vertical' | 'horizontal';
}) => {
  const { departments } = useStores().departmentStore;

  const [department, setDepartment] = useState<IDepartment | undefined>(undefined);

  const hq = department?.pnm || department?.deptPnm;
  const upper = hq ? String(hq).toUpperCase() : '';
  const isCEO = upper.includes('CEO');
  const hqText = isCEO ? '' : String(upper);

  useEffect(() => {
    if (deptSid) {
      const department = departments.find((dept) => dept.deptSid === deptSid);
      setDepartment(department);
    }
  }, [deptSid, departments]);

  return (
    <Box
      display="flex"
      height={'100%'}
      flexDirection={orientation === 'vertical' ? 'column' : 'row'}
      alignItems={orientation === 'vertical' ? 'flex-start' : 'center'}
      justifyContent={orientation === 'vertical' ? 'center' : 'flex-start'}
      gap={orientation === 'vertical' ? 0.5 : 1}
      fontSize={16}
      lineHeight={1}
    >
      {hqText && (
        <Typography variant="caption" color="text.secondary">
          {`${hqText} `}
        </Typography>
      )}
      {department?.deptNm || ''}
    </Box>
  );
};
